import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditSuburb from "./AddEditSuburb";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { FilterIcon, Loader2, LucidePlusCircle } from "lucide-react";
import SuburbActionButton from "./SuburbActionButton";
import Confirmation from "../../../components/Confirmation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import CustomLabelText from "../../../components/CustomLabelText";
import SearchSelect from "../../../components/SearchSelect";
import { useStateList } from "../../../lib/OptionsAPI";
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu";

const Suburbs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [suburbsList, setSuburbsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [suburbId, setSuburbId] = useState(null);
  const [showAddEditSuburb, setShowAddEditSuburb] = useState(false);
  const [showSuburbDelete, setShowSuburbDelete] = useState(false);

  const [stateId, setStateId] = useState();

  // State
  const { stateLoading, stateList = [] } = useStateList();
  const stateListing = stateList?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name + ` (${short_name})`, value, ...rest };
  });

  const toggleShowAddEditSuburb = () => {
    if (showAddEditSuburb) {
      setEdit(false);
      setSuburbId(null);
    }
    setShowAddEditSuburb(!showAddEditSuburb);
  };

  const toggleShowDelete = () => {
    if (showSuburbDelete) {
      setSuburbId(null);
    }
    setShowSuburbDelete(!showSuburbDelete);
  };

  const fetchSuburb = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_SUBURB_LIST}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&state_id=${stateId ? stateId : ""}`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const suburbsList = data.data.data;
        setSuburbsList(suburbsList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setSuburbsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, stateId]
  );

  const suburbsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: suburbsList[i]?.id,
        state: suburbsList[i]?.state?.name
          ? suburbsList[i]?.state?.name
          : "-",
        name: suburbsList[i]?.name ? suburbsList[i]?.name : "",
        action: (
          <>
            <SuburbActionButton
              id={suburbsList[i]?.id}
              setEdit={setEdit}
              is_used={suburbsList[i]?.is_used}
              setSuburbId={setSuburbId}
              toggleShowAddEditSuburb={toggleShowAddEditSuburb}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const suburbs = suburbsGenerator(suburbsList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "state",
      text: "State",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchSuburb();
  }, [fetchSuburb]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchSuburb(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchSuburb(limit, page);
  };

  // Single Suburb Delete
  const handleSuburbDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.GET_ADMIN_SUBURB_LIST}/${suburbId}`);
      setLoading(false);
      fetchSuburb();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="px-2">
                  <FilterIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[250px]">
                <DropdownMenuItem
                  className="focus:bg-transparent block"
                  asChild
                >
                  <div
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.preventDefault()}
                  >
                    <div className="grid gap-2">
                      <div className="min-w-0">
                        <Label htmlFor="state_id">
                          <CustomLabelText required={false} labelText="State" />
                        </Label>
                        <div className="relative mt-2">
                          <SearchSelect
                            frameworks={stateListing}
                            optionType={"State"}
                            clearable={true}
                            onClear={() => {
                              setStateId("");
                            }}
                            handleSelect={(e) => {
                              setStateId(e);
                            }}
                            disabled={false}
                            initialValue={stateId}
                          />

                          {stateLoading && (
                            <span className="absolute top-2 right-2 animate-spin">
                              <Loader2 width={20} height={20} />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      setStateId("");
                    }}
                    className="text-red-500 focus:bg-destructive/10"
                  >
                    Reset
                  </DropdownMenuItem>
                </>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Add Suburb */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Suburb" : "Add Suburb"}
              handleOpen={() => {}}
              handleClose={() => {}}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Suburb</span>
                </div>
              }
              className={"sm:max-w-3/4"}
              open={showAddEditSuburb}
              buttonClassName={""}
              setOpen={setShowAddEditSuburb}
            >
              <AddEditSuburb
                edit={edit}
                toggleShowAddEditSuburb={toggleShowAddEditSuburb}
                id={suburbId}
                fetchSuburbData={() => fetchSuburb(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={suburbs}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Suburb Naming"}
        open={showSuburbDelete}
        setOpen={setShowSuburbDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete suburb?"}
        okAction={async () => {
          handleSuburbDelete();
          toggleShowDelete();
          fetchSuburb();
        }}
        cancelAction={() => {
          setSuburbId(null);
          setShowSuburbDelete(!showSuburbDelete);
        }}
      />
    </>
  );
};

export default Suburbs;
